import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Check } from "lucide-react";
import type { Plan } from "../../plans";
import { plans } from "../../plans";

type CheckoutPlanSummaryProps = {
  planId: string;
};

function findPlanById(planId: string): Plan | undefined {
  return plans.find((plan) => plan.id === planId);
}

export function CheckoutPlanSummary({ planId }: CheckoutPlanSummaryProps) {
  const selectedPlan = findPlanById(planId);

  if (!selectedPlan) {
    return (
      <Card className="border-destructive/20 bg-destructive/5">
        <CardContent className="p-6">
          <p className="text-center text-destructive">Plan not found</p>
        </CardContent>
      </Card>
    );
  }

  const {
    name,
    description,
    price,
    priceDetail,
    features,
    color,
    bgImage,
    isMostPopular,
  } = selectedPlan;

  return (
    <Card className={cn("relative overflow-hidden", `border-${color}/20`)}>
      <CardHeader className="relative pb-4">
        {/* Background decoration */}
        <div className="pointer-events-none absolute top-4 right-4 h-16 w-16 opacity-20">
          <img
            alt=""
            className="h-full w-full object-contain"
            height={64}
            src={bgImage}
            width={64}
          />
        </div>

        <div className="relative z-10">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2 font-serif text-xl">
                {name}
                {!!isMostPopular && (
                  <Badge className="bg-red-500 px-2 py-0.5 text-white text-xs">
                    MOST POPULAR
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1 text-sm">
                {description}
              </CardDescription>
            </div>
          </div>

          <div className="mt-3">
            <div className="flex items-baseline gap-1">
              <span
                className={cn(
                  "font-normal",
                  price.includes("$") ? "text-3xl" : "text-2xl"
                )}
              >
                {price}
              </span>
              {!!priceDetail && (
                <span className="text-muted-foreground text-xs uppercase tracking-wider">
                  {priceDetail}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="pt-4">
        <h4 className="mb-3 font-medium text-muted-foreground text-sm uppercase tracking-wide">
          What's included
        </h4>
        <div className="space-y-4">
          {features.map((group, idx) => (
            <div key={`${name}-group-${group[0]}`}>
              {idx > 0 && <Separator className="my-2" />}
              <div className="space-y-2">
                {group.map((feature) => {
                  // Check if this is an "Everything in..." line
                  const isEverythingLine =
                    feature.includes("Everything in") && feature.endsWith(":");

                  if (isEverythingLine) {
                    // Render as section header without check mark
                    return (
                      <div
                        className="mb-1 font-medium text-foreground text-sm"
                        key={feature}
                      >
                        {feature}
                      </div>
                    );
                  }

                  return (
                    <div
                      className="flex items-start gap-2 text-sm"
                      key={feature}
                    >
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                      <span className="text-muted-foreground leading-tight">
                        {feature}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
